package ui.campaign.detailv2.editor.page

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import compose.assets.Svg
import compose.assets.svg.Calendar
import compose.assets.svg.Clock_outline
import compose.assets.svg.Eye_outline
import compose.assets.svg.Palette
import kotlinx.datetime.Clock
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import ssoft.frontend.generated.resources.*
import tools.formatFromRes
import tools.rememberString
import ui.campaign.detailv2.editor.CampaignEditorScope
import ui.campaign.detailv2.editor.NextButton
import ui.campaign.detailv2.editor.wrapper.CampaignWrapperE
import ui.widget.*

private val contentSize = 512.dp

@Composable
fun CampaignEditorScope.CampaignPage07MiniApp(nnvl: CampaignWrapperE) {
    val state = rememberLazyListState()
    val stateDate = DialogState.remember()
    val stateTime = DialogState.remember()

    LaunchedEffect(Unit) {
        nnvl.miniApp.setupLabels()
    }

    Box(Modifier.fillMaxSize()) {
        LazyColumn(
            state = state,
            modifier = Modifier.fillMaxSize(),
            contentPadding = PaddingValues(vertical = 16.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            item {
                Card(
                    colors = CardDefaults.cardColors(
                        containerColor = MaterialTheme.colorScheme.surfaceContainer
                    )
                ) {
                    Column(modifier = Modifier.padding(16.dp).width(contentSize)) {
                        Text(
                            text = Res.string.layer_top_detail_editor__miniapp_title.rememberString(),
                            style = MaterialTheme.typography.titleLarge,
                            fontWeight = FontWeight.Bold
                        )
                        Spacer(modifier = Modifier.height(16.dp))

                        SwitchCard(
                            field = nnvl.miniApp.enabled,
                            label = Res.string.layer_top_detail_editor__miniapp_enabled
                        )

                        AnimatedVisibility(visible = nnvl.miniApp.enabled.state) {

                            Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                                Spacer(modifier = Modifier.height(8.dp))

                                // Date Selection
                                EditorCardWrapper(
                                    icon = Svg.Calendar,
                                    title = Res.string.layer_top_detail_editor__miniapp_target_date,
                                    tooltip = Res.string.layer_top_detail_editor__miniapp_target_date_tooltip
                                ) {
                                    Button(
                                        onClick = { stateDate.show() },
                                        modifier = Modifier.fillMaxWidth()
                                    ) {
                                        Text(
                                            text = nnvl.miniApp.datetime.state.date.formatFromRes(),
                                            fontWeight = FontWeight.Bold
                                        )
                                    }
                                }

                                // Time Selection
                                val currentDateTime = nnvl.miniApp.datetime.state

                                EditorCardWrapper(
                                    icon = Svg.Clock_outline,
                                    title = Res.string.layer_top_detail_editor__miniapp_target_time.rememberString(
                                        currentDateTime.hour.toString().padStart(2, '0'),
                                        currentDateTime.minute.toString().padStart(2, '0')
                                    ),
                                    tooltip = Res.string.layer_top_detail_editor__miniapp_target_time_tooltip,
                                    tooltipTitle = Res.string.layer_top_detail_editor__miniapp_target_time_label
                                ) {
                                    Button(
                                        onClick = { stateTime.show() },
                                        modifier = Modifier.fillMaxWidth()
                                    ) {
                                        Text(
                                            text = "${
                                                currentDateTime.hour.toString().padStart(2, '0')
                                            }:${currentDateTime.minute.toString().padStart(2, '0')}",
                                            fontWeight = FontWeight.Bold
                                        )
                                    }
                                }

                                // Colors Selection
                                EditorCardWrapper(
                                    icon = Svg.Palette,
                                    title = Res.string.layer_top_detail_editor__miniapp_card_appearance,
                                    tooltip = Res.string.layer_top_detail_editor__miniapp_card_appearance_tooltip
                                ) {
                                    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                                        ColorField(
                                            field = nnvl.miniApp.cardSurfaceColor,
                                            label = Res.string.layer_bottom_wheel_page_theme_items_color_bg,
                                            modifier = Modifier.fillMaxWidth()
                                        )

                                        ColorField(
                                            field = nnvl.miniApp.cardOnSurfaceColor,
                                            label = Res.string.layer_bottom_wheel_page_theme_items_color_fg,
                                            modifier = Modifier.fillMaxWidth()
                                        )
                                    }
                                }
                                EditorCardWrapper(
                                    icon = Svg.Eye_outline,
                                    title = Res.string.layer_top_detail_editor__miniapp_preview_title,
                                    tooltip = Res.string.layer_top_detail_editor__miniapp_preview_title_tooltip
                                ) {
                                    var timeLeft by remember(nnvl.miniApp.datetime.state) {
                                        mutableStateOf(calculateTimeLeftComponents(nnvl.miniApp.datetime.state))
                                    }

                                    LaunchedEffect(nnvl.miniApp.datetime.state) {
                                        while (true) {
                                            timeLeft = calculateTimeLeftComponents(nnvl.miniApp.datetime.state)
                                            kotlinx.coroutines.delay(1000)
                                        }
                                    }

                                    val contentColor = nnvl.miniApp.cardOnSurfaceColor.state
                                    val surfaceColor = nnvl.miniApp.cardSurfaceColor.state

                                    if (!timeLeft.error && !timeLeft.ended) {
                                        Card(
                                            shape = RoundedCornerShape(8.dp),
                                            colors = CardDefaults.cardColors(
                                                containerColor = surfaceColor,
                                                contentColor = contentColor
                                            ),
                                            elevation = CardDefaults.cardElevation(16.dp),
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .border(
                                                    width = 1.dp,
                                                    color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.15f),
                                                    shape = RoundedCornerShape(8.dp)
                                                )
                                        ) {
                                            Row(
                                                verticalAlignment = Alignment.Top,
                                                horizontalArrangement = Arrangement.Center,
                                                modifier = Modifier
                                                    .fillMaxWidth()
                                                    .padding(horizontal = 32.dp, vertical = 20.dp)
                                            ) {
                                                PreviewTimeSegment(
                                                    value = timeLeft.days,
                                                    label = nnvl.miniApp.labels.days,
                                                    color = contentColor
                                                )
                                                PreviewColonSeparator(color = contentColor)
                                                PreviewTimeSegment(
                                                    value = timeLeft.hours,
                                                    label = nnvl.miniApp.labels.hours,
                                                    color = contentColor
                                                )
                                                PreviewColonSeparator(color = contentColor)
                                                PreviewTimeSegment(
                                                    value = timeLeft.minutes,
                                                    label = nnvl.miniApp.labels.minutes,
                                                    color = contentColor
                                                )
                                                PreviewColonSeparator(color = contentColor)
                                                PreviewTimeSegment(
                                                    value = timeLeft.seconds,
                                                    label = nnvl.miniApp.labels.seconds,
                                                    color = contentColor
                                                )
                                            }
                                        }
                                    } else {
                                        Card(
                                            shape = RoundedCornerShape(8.dp),
                                            colors = CardDefaults.cardColors(
                                                containerColor = surfaceColor,
                                                contentColor = contentColor
                                            ),
                                            elevation = CardDefaults.cardElevation(16.dp),
                                            modifier = Modifier.fillMaxWidth()
                                        ) {
                                            Box(
                                                modifier = Modifier
                                                    .fillMaxWidth()
                                                    .padding(horizontal = 32.dp, vertical = 20.dp),
                                                contentAlignment = Alignment.Center
                                            ) {
                                                Text(
                                                    text = nnvl.miniApp.labels.ended,
                                                    style = MaterialTheme.typography.headlineMedium,
                                                    fontWeight = FontWeight.Bold,
                                                    textAlign = TextAlign.Center,
                                                    color = contentColor
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
            item {
                NextButton()
            }
        }
    }

    DatePickerDialog(
        state = stateDate,
        date = nnvl.miniApp.datetime
    )

    TimePickerDialog(
        state = stateTime,
        field = nnvl.miniApp.datetime
    )
}

private data class TimeLeftComponents(
    val days: Long,
    val hours: Long,
    val minutes: Long,
    val seconds: Long,
    val ended: Boolean,
    val error: Boolean
)

private fun calculateTimeLeftComponents(target: LocalDateTime): TimeLeftComponents {
    val nowInstant = Clock.System.now()
    val targetInstant = try {
        target.toInstant(TimeZone.currentSystemDefault())
    } catch (e: Exception) {
        return TimeLeftComponents(0, 0, 0, 0, ended = true, error = true)
    }
    val diffMs = targetInstant.toEpochMilliseconds() - nowInstant.toEpochMilliseconds()
    if (diffMs <= 0) return TimeLeftComponents(0, 0, 0, 0, ended = true, error = false)
    val diffSeconds = diffMs / 1000
    val days = diffSeconds / (24 * 3600)
    val hours = (diffSeconds % (24 * 3600)) / 3600
    val minutes = (diffSeconds % 3600) / 60
    val secs = diffSeconds % 60
    return TimeLeftComponents(days, hours, minutes, secs, ended = false, error = false)
}

@Composable
private fun PreviewTimeSegment(value: Long, label: String, color: androidx.compose.ui.graphics.Color) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = Modifier.width(64.dp)
    ) {
        Text(
            text = value.toString().padStart(2, '0'),
            style = MaterialTheme.typography.displaySmall.copy(
                fontWeight = FontWeight.Bold,
                color = color
            ),
            textAlign = TextAlign.Center
        )
        Spacer(modifier = Modifier.height(4.dp))
        Text(
            text = label,
            style = MaterialTheme.typography.bodyMedium.copy(
                fontWeight = FontWeight.Medium,
                color = color.copy(alpha = 0.5f)
            ),
            textAlign = TextAlign.Center
        )
    }
}

@Composable
private fun PreviewColonSeparator(color: androidx.compose.ui.graphics.Color) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Top
    ) {
        Text(
            text = ":",
            style = MaterialTheme.typography.displaySmall.copy(
                fontWeight = FontWeight.Light,
                color = color.copy(alpha = 0.5f)
            ),
            textAlign = TextAlign.Center
        )
        Spacer(modifier = Modifier.height(4.dp))
        Text(
            text = "",
            style = MaterialTheme.typography.bodyMedium
        )
    }
}
